// Landforms: spires, hoodoos, stone arches (+ natural canyon bridges) and floating shelves with
// their hanging keels, drawn at exactly the sim's footprints (sim/landforms.ts, world.cyls /
// world.boxes kinds spire, archleg, arch, shelf, keel). Returns vertex-coloured geometry; the
// world view draws `rock` with the island strata material and `tops` with the island-top (grass)
// material, so they read as carved from the same rock as the islands.
//
// Round 2 (James's standing rule: every stone organic, nothing blocky): each landform is ONE
// continuous eroded surface, not a stack of prisms. Columns are lathed rings (12-18 sides) whose
// radius carries two scales of noise, a wobbling/leaning centre line, necks, bulges and a flared
// talus skirt; strata are WAVERING LEDGES -- radius bumps on tilted, noisy bedding lines whose
// thickness and overhang vary round the column and that fade out part-way round, each with a
// shadowed groove below. Arch legs flare out of the ground and sweep (elliptical section) up into
// a lofted span whose underside sags into an arched opening and thins at mid-span. Shelves are
// lumpy rounded plates; keels taper in wavering steps to a dripping point. Low-poly faceting is
// kept (flat face normals), regularity is not.
//
// One shape function per kind (spire, hoodoo, archLeg, archDeck, shelf, keel), each built only
// from its own Cyl/Box (plus caller-supplied hints). All noise, ledges and colour are functions of
// WORLD position and the landform's own seed, so a shortened copy (a toppled chunk) is the same rock.
//
// Value only: the strata shader takes its hue from world-y sedimentary bands and reads the vertex
// colour's luminance as a ratio against the plain side colour (cRockEarth, lum ~0.046), except
// greenish colours (r < g), which keep their own colour: moss, grass skirts, teal crevices.

import * as THREE from 'three';
import type { World, Cyl, Box } from '../../../sim/world';
import { hash32, valueNoise, smoothstep } from '../../../sim/core';
import { PAL } from '../palette';
import { Geo, C, weldNormals } from './geo';

export interface LandformGeo { rock: THREE.BufferGeometry; tops: THREE.BufferGeometry }

const TAU = Math.PI * 2;
// the island strata's own colours (terrain.ts)
const cRockEarth = new THREE.Color(0x4a3a2b), cRockDark = new THREE.Color(0x262624);
const cWarmRim = new THREE.Color(0xc98a3e);
const cGreen = C(PAL.grassGreen), cGreenDeep = C(PAL.grassGreenDeep), cUnder = C(PAL.underside), cDirt = C(PAL.dirt);
const cFloor = new THREE.Color(0xd8a030), cFloorLight = new THREE.Color(0xf0bc4c), cFloorGreen = new THREE.Color(0xa8a23c);
const cMoss = new THREE.Color(0x5f7a34), cMossDeep = new THREE.Color(0x2f4d3c);
const cOliveEdge = new THREE.Color(0x76802e);
// crevices and undersides go cool teal-dark (greenish, so the strata shader keeps their colour)
const cSeamTeal = new THREE.Color(0x1d2b28), cUnderTeal = new THREE.Color(0x3a5048);
const cSkirt = new THREE.Color(0x5f6428).lerp(cMossDeep, 0.15); // olive, never lime
const cSkirtLow = new THREE.Color().copy(cMossDeep).lerp(cUnder, 0.3);

type V = number[];
const QK = [0.55, 0.75, 1, 1.2];
const DY = [1.0, 0.7, 0.42, 0.34];

const k01 = (h: number) => (h & 0xffff) / 0xffff;
/** periodic-in-angle noise that also varies with world height */
function nA(seed: number, th: number, y: number, f: number, ys: number) {
  return valueNoise(seed, Math.cos(th) * f + y / ys * 0.61 + 31.7, Math.sin(th) * f - y / ys * 0.79 + 12.3);
}

// ------------------------------------------------------------------ emit helpers
const _e1 = new THREE.Vector3(), _e2 = new THREE.Vector3(), _nn = new THREE.Vector3();
/** Triangle with per-vertex colours, wound so its face normal points along (ox, oy, oz). */
function tri(g: Geo, a: V, b: V, c: V, ca: THREE.Color, cb: THREE.Color, cc: THREE.Color, ox: number, oy: number, oz: number) {
  _e1.set(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
  _e2.set(c[0] - a[0], c[1] - a[1], c[2] - a[2]);
  _nn.crossVectors(_e1, _e2);
  if (_nn.lengthSq() < 1e-12) return;
  if (_nn.x * ox + _nn.y * oy + _nn.z * oz >= 0) {
    g.pos.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
    g.col.push(ca.r, ca.g, ca.b, cb.r, cb.g, cb.b, cc.r, cc.g, cc.b);
  } else {
    g.pos.push(a[0], a[1], a[2], c[0], c[1], c[2], b[0], b[1], b[2]);
    g.col.push(ca.r, ca.g, ca.b, cc.r, cc.g, cc.b, cb.r, cb.g, cb.b);
  }
}

// ------------------------------------------------------------------ ledges (wavering strata)
/**
 * Bedding ledges in world y: one candidate per cell of height `G` (so a shortened copy keeps the
 * same ledges). Each is tilted, wanders with angle, varies in thickness and overhang round the
 * column, and (often) fades out part-way round. Returns the radial offset (fraction of radius)
 * and how deep in a shaded groove the point sits.
 */
interface LedgeSet { seed: number; G: number; o0: number; o1: number; partial: number; keep: number }
const _lg = { g: 0, groove: 0 };
function ledgeAt(L: LedgeSet, th: number, y: number) {
  _lg.g = 0; _lg.groove = 0;
  const k0 = Math.floor(y / L.G);
  for (let k = k0 - 1; k <= k0 + 1; k++) {
    const h = hash32(L.seed, k, 0x1ed6);
    if (k01(h) > L.keep) continue;
    const yl = (k + 0.15 + 0.7 * k01(h >>> 5)) * L.G;
    const tilt = (0.1 + 0.45 * k01(h >>> 9)) * Math.min(1.5, L.G / 3);
    const tp = k01(h >>> 13) * TAU;
    const line = yl + tilt * Math.cos(th - tp) + (nA(h & 0xfff, th, 0, 2.3, 1) - 0.5) * 0.7;
    const d = line - y; // > 0: below the ledge's top line
    if (d < -0.4 || d > 2.6) continue;
    const partial = k01(h >>> 3) < L.partial;
    const m = partial ? smoothstep(-0.25, 0.35, Math.cos(th - k01(h >>> 17) * TAU) + (k01(h >>> 21) - 0.5) * 0.8) : 1;
    if (m <= 0) continue;
    const tt = (0.35 + 0.5 * k01(h >>> 7)) * (0.55 + 0.9 * nA((h >>> 4) & 0xfff, th, 0, 1.7, 1));
    const oo = (L.o0 + (L.o1 - L.o0) * k01(h >>> 11)) * (0.35 + 1.1 * nA((h >>> 8) & 0xfff, th, 0, 2.6, 1));
    let g: number, gr = 0;
    if (d < 0) g = oo * (1 + d / 0.4);
    else if (d < tt) g = oo * (1 - 0.3 * d / tt);
    else if (d < tt + 1.3) { const u = (d - tt) / 1.3; g = -oo * 0.6 * (1 - u) * (1 - u * 0.3); gr = 1 - u; }
    else g = 0;
    _lg.g += g * m; _lg.groove = Math.max(_lg.groove, gr * m);
  }
  return _lg;
}

// ------------------------------------------------------------------ colour
interface Paint {
  seed: number; ground: number; top: number;
  /** metres below `top` painted as grass skirt (0 = none) */
  grass: number;
  /** below this y the rock cools and darkens toward `coolTo` (keels) */
  coolFrom?: number; coolTo?: number;
  moss: number;
}
const _side = new THREE.Color(), _lip = new THREE.Color();
function paint(P: Paint, x: number, y: number, z: number, th: number, ny: number, groove: number, out: THREE.Color) {
  const span = Math.max(1, P.top - P.ground);
  const lift = smoothstep(P.ground, P.ground + Math.min(12, span * 0.7), y);
  const n = valueNoise(P.seed + 3, x / 2.2 + y / 3.1, z / 2.2 - y / 2.7);
  _side.copy(cRockEarth).multiplyScalar((0.74 + 0.3 * lift) * (0.8 + 0.4 * n));
  _side.multiplyScalar(0.62 + 0.38 * smoothstep(P.ground - 0.5, P.ground + 2.5, y)); // contact AO
  if (P.coolFrom !== undefined && P.coolTo !== undefined && y < P.coolFrom) {
    const t = smoothstep(P.coolFrom, P.coolTo, y);
    // darken only, never toward teal: a cool vertex colour (r < g) trips the strata shader's
    // grass-skirt rule and the rock falls back to its flat, dark vertex colour (the black keels)
    _side.lerp(cRockDark, 0.12 + 0.2 * t);
  }
  out.copy(_side);
  // ledge tops: warm sunlit lips, mossed over in patches
  const lipAmt = smoothstep(0.22, 0.62, ny);
  if (lipAmt > 0) {
    const mn = valueNoise(P.seed + 11, x / 3.3, z / 3.3 + y * 0.21);
    if (mn < P.moss) _lip.copy(cMoss).lerp(cMossDeep, n * 0.6);
    else _lip.copy(cWarmRim).lerp(_side, 0.22).multiplyScalar(1.1 * (0.85 + 0.3 * n));
    out.lerp(_lip, lipAmt);
  }
  // grooves under the ledges and overhang undersides: cool teal-dark
  if (groove > 0) out.lerp(_lip.copy(cSeamTeal).multiplyScalar(0.8 + 5 * _side.g), groove * 0.75);
  // undersides: a warm bounce-lit rock (the strata shader darkens down-faces itself; a teal
  // colour would bypass it and read as mud)
  out.lerp(_lip.copy(cRockEarth).lerp(cWarmRim, 0.25), smoothstep(-0.12, -0.55, ny) * 0.5);
  // the crown's grass skirt, hanging in irregular tongues
  if (P.grass > 0) {
    const tongue = P.grass * (0.25 + 1.5 * valueNoise(P.seed + 17, x / 1.1 + z * 0.3, z / 1.1 - x * 0.3) ** 1.6 + 0.3 * valueNoise(P.seed + 19, x * 1.7, z * 1.7));
    const d = P.top - y;
    if (d < tongue) out.copy(cSkirt).lerp(cSkirtLow, smoothstep(tongue * 0.4, tongue, d));
  }
  return out;
}

// ------------------------------------------------------------------ grids
/**
 * Emit a ring grid (rings top -> bottom, `wrap` = closed around) with per-vertex colours. Normals
 * for colouring and winding are central differences, flipped to face away from `axis(i)`.
 */
function emitGrid(rock: Geo, P: V[][], cols: THREE.Color[][], wrap: boolean, axis: (i: number, j: number) => V) {
  const nI = P.length, nJ = P[0].length;
  const jn = (j: number) => (wrap ? (j + 1) % nJ : j + 1);
  for (let i = 0; i < nI - 1; i++) {
    for (let j = 0; j < (wrap ? nJ : nJ - 1); j++) {
      const a = P[i][j], b = P[i][jn(j)], c = P[i + 1][jn(j)], d = P[i + 1][j];
      const mx = (a[0] + b[0] + c[0] + d[0]) / 4, my = (a[1] + b[1] + c[1] + d[1]) / 4, mz = (a[2] + b[2] + c[2] + d[2]) / 4;
      const ax = axis(i, j);
      const ox = mx - ax[0], oy = my - ax[1], oz = mz - ax[2];
      tri(rock, a, b, c, cols[i][j], cols[i][jn(j)], cols[i + 1][jn(j)], ox, oy, oz);
      tri(rock, a, c, d, cols[i][j], cols[i + 1][jn(j)], cols[i + 1][j], ox, oy, oz);
    }
  }
}
const _t1 = new THREE.Vector3(), _t2 = new THREE.Vector3(), _nv = new THREE.Vector3();
/** Unit normal ny at grid vertex (i, j) from central differences, oriented away from `ax`. */
function gridNy(P: V[][], i: number, j: number, wrap: boolean, ax: V) {
  const nI = P.length, nJ = P[0].length;
  const jm = wrap ? (j - 1 + nJ) % nJ : Math.max(0, j - 1), jp = wrap ? (j + 1) % nJ : Math.min(nJ - 1, j + 1);
  const im = Math.max(0, i - 1), ip = Math.min(nI - 1, i + 1);
  const A = P[i][jp], B = P[i][jm], U = P[im][j], D = P[ip][j];
  _t1.set(A[0] - B[0], A[1] - B[1], A[2] - B[2]);
  _t2.set(U[0] - D[0], U[1] - D[1], U[2] - D[2]);
  _nv.crossVectors(_t1, _t2);
  const p = P[i][j];
  if (_nv.x * (p[0] - ax[0]) + _nv.y * (p[1] - ax[1]) + _nv.z * (p[2] - ax[2]) < 0) _nv.negate();
  const l = _nv.length();
  return l > 1e-9 ? _nv.y / l : 0;
}

// ------------------------------------------------------------------ columns (spire, hoodoo, leg, shelf, keel)
interface Column {
  seed: number; sides: number;
  ys: number[]; // top -> bottom
  centre: (y: number, out: number[]) => void;
  radius: (th: number, y: number) => number; // before ledges
  R: number; maxR: number; minR: number; bx: number; bz: number;
  ledges: LedgeSet | null;
  paint: Paint;
  /** grassy cap at ys[0] in `tops` (else a rock cap / none) */
  crown: 'tops' | 'rock' | 'none';
  /** close the bottom with a fan (floating rock) */
  bottomCap: boolean;
  /** drop the bottom fan's centre this far below the last ring (a domed underside) */
  bottomDip?: number;
}
const _ctr = [0, 0];
function buildColumn(rock: Geo, tops: Geo, K: Column) {
  const nJ = K.sides;
  const th: number[] = [];
  for (let j = 0; j < nJ; j++) th.push(((j + (k01(hash32(K.seed, j, 0xa9)) - 0.5) * 0.5) / nJ) * TAU);
  const P: V[][] = [], G: number[][] = [], ctrs: V[] = [];
  for (const y of K.ys) {
    K.centre(y, _ctr);
    const cx = _ctr[0], cz = _ctr[1];
    ctrs.push([cx, y, cz]);
    const row: V[] = [], gr: number[] = [];
    for (let j = 0; j < nJ; j++) {
      let r = K.radius(th[j], y);
      let groove = 0;
      if (K.ledges && r > 0) { const L = ledgeAt(K.ledges, th[j], y); r *= 1 + L.g; groove = L.groove; }
      // visible rock stays inside ~1.1 r of the sim's footprint, drift included
      const dd = Math.hypot(cx - K.bx, cz - K.bz);
      if (r > 0) r = Math.min(K.maxR - dd, Math.max(K.minR, r * (1 - (0.95 * dd) / K.R)));
      row.push([cx + Math.cos(th[j]) * r, y, cz + Math.sin(th[j]) * r]);
      gr.push(groove);
    }
    P.push(row); G.push(gr);
  }
  const cols: THREE.Color[][] = [];
  for (let i = 0; i < P.length; i++) {
    const row: THREE.Color[] = [];
    for (let j = 0; j < nJ; j++) {
      const p = P[i][j];
      const ny = gridNy(P, i, j, true, ctrs[i]);
      row.push(paint(K.paint, p[0], p[1], p[2], th[j], ny, G[i][j], new THREE.Color()));
    }
    cols.push(row);
  }
  emitGrid(rock, P, cols, true, (i) => ctrs[Math.min(i + 1, ctrs.length - 1)]);
  const top = P[0], ct = ctrs[0];
  if (K.crown === 'tops') crown(tops, ct[0], ct[1], ct[2], top, K.seed);
  else if (K.crown === 'rock') for (let j = 0; j < nJ; j++) tri(rock, ct, top[j], top[(j + 1) % nJ], cols[0][j], cols[0][j], cols[0][(j + 1) % nJ], 0, 1, 0);
  if (K.bottomCap) {
    // a noise-dented two-ring dome (one flat fan read as a polygon); the bowl's value comes from
    // the strata shader's down-face bounce term
    const bot = P[P.length - 1], c0 = ctrs[ctrs.length - 1], dip = K.bottomDip ?? 0;
    const cb = [c0[0], c0[1] - dip, c0[2]];
    const cl = cols[cols.length - 1];
    const L0 = cl, cC = new THREE.Color().copy(cl[0]).lerp(cRockEarth, 0.3);
    const mid: V[] = [], M: THREE.Color[] = [];
    for (let j = 0; j < nJ; j++) {
      const b = bot[j], th = (j / nJ) * TAU;
      const f = 0.55 + (nA(K.seed + 41, th, 0, 2.2, 9) - 0.5) * 0.16;
      const d = dip * (0.72 + (nA(K.seed + 42, th, 0, 3.1, 9) - 0.5) * 0.5);
      mid.push([cb[0] + (b[0] - cb[0]) * f, b[1] - d, cb[2] + (b[2] - cb[2]) * f]);
      M.push(new THREE.Color().copy(L0[j]).lerp(cC, 0.5));
    }
    for (let j = 0; j < nJ; j++) {
      const k = (j + 1) % nJ;
      tri(rock, mid[j], bot[j], bot[k], M[j], L0[j], L0[k], 0, -1, 0);
      tri(rock, mid[j], bot[k], mid[k], M[j], L0[k], M[k], 0, -1, 0);
      tri(rock, cb, mid[j], mid[k], cC, M[j], M[k], 0, -1, 0);
    }
  }
}

/** The grassy crown: golden meadow in the middle going deep green toward the lip. */
const cc0 = new THREE.Color(), cc1 = new THREE.Color(), cc2 = new THREE.Color(), cc3 = new THREE.Color(), cc4 = new THREE.Color();
function crownCol(x: number, z: number, edge: number, seed: number, out: THREE.Color) {
  const n = valueNoise(seed ^ 0x51, x / 4, z / 4), m = valueNoise(seed ^ 0x77, x / 2.5 + 9, z / 2.5);
  out.copy(cFloor).lerp(cFloorLight, n * 0.6).lerp(cFloorGreen, smoothstep(0.35, 0.8, m) * 0.7);
  out.lerp(cOliveEdge, smoothstep(0.45, 1.0, edge) * 0.6);
  return out.multiplyScalar(0.94 + 0.12 * n);
}
function crown(tops: Geo, cx: number, y: number, cz: number, ring: V[], seed: number) {
  const n = ring.length;
  const C0 = [cx, y + 0.02, cz];
  crownCol(cx, cz, 0, seed, cc0);
  for (let i = 0; i < n; i++) {
    const p = ring[i], q = ring[(i + 1) % n];
    const P = [cx + (p[0] - cx) * 1.01, y + 0.02, cz + (p[2] - cz) * 1.01];
    const Q = [cx + (q[0] - cx) * 1.01, y + 0.02, cz + (q[2] - cz) * 1.01];
    const Pi = [cx + (P[0] - cx) * 0.55, y + 0.02, cz + (P[2] - cz) * 0.55];
    const Qi = [cx + (Q[0] - cx) * 0.55, y + 0.02, cz + (Q[2] - cz) * 0.55];
    crownCol(P[0], P[2], 1, seed, cc1); crownCol(Q[0], Q[2], 1, seed, cc2);
    crownCol(Pi[0], Pi[2], 0.55, seed, cc3); crownCol(Qi[0], Qi[2], 0.55, seed, cc4);
    tri(tops, C0, Pi, Qi, cc0, cc3, cc4, 0, 1, 0);
    tri(tops, Pi, P, Q, cc3, cc1, cc2, 0, 1, 0);
    tri(tops, Pi, Q, Qi, cc3, cc2, cc4, 0, 1, 0);
  }
}

/** Ring heights: the crown, a bevel ring just under it, then every `dy` down to y0. */
function ringsY(y1: number, y0: number, dy: number, bevel = 0.3) {
  const ys = [y1];
  let y = y1 - Math.min(bevel, (y1 - y0) * 0.3);
  while (y > y0 + dy * 0.4) { ys.push(y); y -= dy; }
  ys.push(y0);
  return ys;
}

/** A toppled drum's slice of its column: rings only inside [y0, y1], closed with broken faces
 *  (a rock fan at each end, or the grassy crown on top when `crown`). */
interface Cut { y0: number; y1: number; crown: boolean }
/** The rings a column (or a cut drum of it) is built on. */
function colRings(top: number, bottom: number, dy: number, bevel: number, cut?: Cut) {
  if (!cut) return ringsY(top, bottom, dy, bevel);
  const y1 = Math.min(cut.y1, top), y0 = Math.max(cut.y0, bottom);
  return ringsY(y1, Math.min(y0, y1 - 0.1), dy, cut.crown ? bevel : 0.12);
}
const cutCrown = (crown: 'tops' | 'rock', cut?: Cut): 'tops' | 'rock' => (cut && !cut.crown ? 'rock' : crown);

/** A wandering centre line: lean (fraction of r per metre of height) plus a slow wobble. */
function wobble(seed: number, x: number, z: number, r: number, amp: number, lean: number, yRef: number) {
  const la = k01(hash32(seed, 0x1ea)) * TAU;
  return (y: number, out: number[]) => {
    const t = y - yRef;
    let dx = Math.cos(la) * lean * r * t + (valueNoise(seed + 21, y / 7.5, 3.3) - 0.5) * 2 * amp * r;
    let dz = Math.sin(la) * lean * r * t + (valueNoise(seed + 22, 5.1, y / 7.5) - 0.5) * 2 * amp * r;
    const d = Math.hypot(dx, dz), cap = r * 0.16;
    if (d > cap) { dx *= cap / d; dz *= cap / d; }
    out[0] = x + dx; out[1] = z + dz;
  };
}

// ---- spire: a tall eroded sandstone stack (sniper perch); crown flat at exactly y1.
// Profile, base to top: a flared talus foot, a fluted shaft with a necked waist, a pinched neck
// under the cap, then a cap-rock wider than the shaft whose rim droops raggedly round the edge.
function capProfile(seed: number, th: number, y: number, y1: number, capH: number, droop: number, tilt = 0, tiltDir = 0) {
  // the cap's lower edge wanders and droops with angle: a ragged rim, not a ruled line; a tilted
  // cap's rim rides up on one side and down on the other
  const yb = y1 - capH - droop * nA(seed + 41, th, 0, 2.1, 1) ** 1.5 + 0.25 * (nA(seed + 42, th, 0, 5.3, 1) - 0.5) + tilt * Math.cos(th - tiltDir);
  const d = yb - y; // > 0 below the rim
  if (d <= 0) return 1.06 + 0.04 * Math.sin(Math.PI * Math.min(1, (y1 - y) / Math.max(0.3, y1 - yb))); // the cap
  // rim: rolls under over ~0.45 m, then the neck
  return 1.06 - 0.24 * smoothstep(0, 0.45, d) + 0.06 * smoothstep(0.6, 2.6, d);
}
function spire(rock: Geo, tops: Geo, c: Cyl, q: number, wseed: number, ground: number, groundAt?: (x: number, z: number) => number, cut?: Cut) {
  const seed = hash32(wseed, 0x5b1e, Math.round(c.x * 13.1) * 7919 + Math.round(c.z * 17.3));
  const R = c.r;
  const span = Math.max(1, c.y1 - ground);
  const tn = 0.35 + 0.25 * k01(hash32(seed, 1)), nd = 0.06 + 0.06 * k01(hash32(seed, 2));
  const capH = Math.min(span * 0.18, 1.6 + 1.2 * k01(hash32(seed, 5))), droop = 0.4 + 0.9 * k01(hash32(seed, 6));
  buildColumn(rock, tops, {
    seed, sides: Math.max(10, Math.round((15 + R * 1.3) * QK[q])),
    ys: colRings(c.y1, c.y0, DY[q], 0.3, cut),
    centre: wobble(seed, c.x, c.z, R, 0.1, 0.006, c.y0),
    radius: (th, y) => {
      const t = (y - ground) / span;
      let e = 0.88 + 0.1 * (valueNoise(seed + 5, y / 6.5, 1.7) - 0.5) * 2 + 0.05 * (valueNoise(seed + 6, y / 2.6, 4.1) - 0.5) * 2;
      e -= nd * Math.exp(-(((t - tn) / 0.1) ** 2));                     // the waist
      e += 0.2 * smoothstep(ground + 3.5, ground - 0.6, y);            // flared talus foot
      const cp = capProfile(seed, th, y, c.y1, capH, droop);
      if (y > c.y1 - capH - 3) e = Math.min(1.08, e + (cp - 0.88) * smoothstep(c.y1 - capH - 3, c.y1 - capH - 1.2, y));
      e += (nA(seed + 7, th, y, 1.2, 4.5) - 0.5) * 0.2 + (nA(seed + 8, th, y, 3.7, 1.7) - 0.5) * 0.08 + (nA(seed + 9, th, y, 6.5, 14) - 0.5) * 0.1;
      if (y > c.y1 - 0.31) e *= 0.94; // rounded crown edge
      return R * e;
    },
    R, maxR: R * 1.1, minR: R * 0.7, bx: c.x, bz: c.z,
    ledges: { seed: seed + 31, G: 2.9, o0: 0.03, o1: 0.15, partial: 0.85, keep: 0.5 },
    paint: { seed, ground, top: c.y1, grass: 1.0, moss: 0.3 },
    crown: cutCrown('tops', cut), bottomCap: !!cut,
  });
  if (groundAt) talus(rock, c.x, c.z, R, seed, q, groundAt, R > 3 ? 1 : 0.6);
}

// ---- hoodoo: a short goblin -- a fat lumpy cap-rock with a drooping rim on a pinched neck
function hoodoo(rock: Geo, tops: Geo, c: Cyl, q: number, wseed: number, ground: number, groundAt?: (x: number, z: number) => number, cut?: Cut) {
  const seed = hash32(wseed, 0x4d00, Math.round(c.x * 13.1) * 7919 + Math.round(c.z * 17.3));
  const R = c.r;
  const span = Math.max(1, c.y1 - ground);
  // each hoodoo its own goblin: cap share of the height, how far the cap flares past the shaft,
  // shaft girth and taper, and a lean (all from its own seed; the crown stays at y1)
  const kH = k01(hash32(seed, 11)), kF = k01(hash32(seed, 12)), kS = k01(hash32(seed, 13)), kL = k01(hash32(seed, 14));
  const capH = Math.min(span * (0.14 + 0.14 * kH), 0.6 + 1.0 * kH + 0.3 * k01(hash32(seed, 1))), droop = 0.35 + 0.8 * k01(hash32(seed, 2));
  const flare = 0.86 + 0.24 * kF;            // cap width vs the template
  const shaft = 0.66 + 0.2 * kS;             // shaft girth
  const taper = (k01(hash32(seed, 15)) - 0.35) * 0.3; // + wider at the foot, - top-heavy
  const neckAt = 0.45 + 0.4 * k01(hash32(seed, 16)); // where the shaft pinches (fraction of height)
  // the cap sits askew (5-12 deg; the crown itself stays flat at y1) and is notched by fallen chunks
  const tilt = R * Math.tan((5 + 7 * k01(hash32(seed, 17))) * Math.PI / 180), tDir = k01(hash32(seed, 18)) * TAU;
  const n1 = k01(hash32(seed, 19)) * TAU, n2 = n1 + 1.6 + 2.5 * k01(hash32(seed, 20));
  const notch = (th: number) => {
    const d1 = Math.atan2(Math.sin(th - n1), Math.cos(th - n1)), d2 = Math.atan2(Math.sin(th - n2), Math.cos(th - n2));
    return 0.2 * Math.exp(-((d1 / 0.28) ** 2)) + 0.13 * Math.exp(-((d2 / 0.2) ** 2));
  };
  buildColumn(rock, tops, {
    seed, sides: Math.max(9, Math.round(15 * QK[q])),
    ys: colRings(c.y1, c.y0, DY[q] * 0.7, 0.22, cut),
    centre: wobble(seed, c.x, c.z, R, 0.1, 0.004 + 0.05 * kL, c.y0),
    radius: (th, y) => {
      const t = Math.max(0, Math.min(1, (y - ground) / span));
      let e = (capProfile(seed, th, y, c.y1, capH, droop, tilt, tDir) - 0.12) * flare; // neck .. cap
      // the cap is a lumpy cushion, not a drum: it swells at mid-height and rolls in to the crown
      if (y > c.y1 - capH - tilt) { const v = Math.max(0, (c.y1 - y) / capH); e *= (0.9 + 0.12 * Math.sin(Math.PI * Math.min(1, v * 1.1))) * (1 - notch(th)); }
      if (y < c.y1 - capH - 1.5) e = shaft + taper * (0.5 - t) - 0.08 * Math.exp(-(((t - neckAt) / 0.12) ** 2)) + 0.07 * (valueNoise(seed + 5, y / 2.2, 1.7) - 0.5) * 2;
      else if (y < c.y1 - capH - 0.6) e = Math.min(e, shaft + taper * (0.5 - t) + 0.04);
      e += 0.24 * smoothstep(ground + 2.2, ground - 0.5, y);            // flared foot
      e += (nA(seed + 7, th, y, 1.4, 3) - 0.5) * 0.16 + (nA(seed + 8, th, y, 3.9, 1.1) - 0.5) * 0.07 + (nA(seed + 9, th, y, 6, 8) - 0.5) * 0.09;
      if (y > c.y1 - 0.23) e *= 0.92;
      return R * e * 1.1;
    },
    R, maxR: R * 1.1, minR: R * 0.5, bx: c.x, bz: c.z,
    ledges: { seed: seed + 31, G: 1.9, o0: 0.04, o1: 0.1, partial: 0.7, keep: 0.6 },
    // bare rock crown with moss in patches (a grass lid read as a drum with a carpet on it)
    paint: { seed, ground, top: c.y1, grass: 0, moss: 0.45 },
    crown: 'rock', bottomCap: !!cut,
  });
  if (groundAt) talus(rock, c.x, c.z, R, seed, q, groundAt, 0.45);
}

// ---- talus apron: a few rounded boulders fallen off the stack, sunk into the ground round the foot
// (set dressing like the meadow pebbles: well under step height or hugging the base)
const _bp = new THREE.Vector3();
function talus(rock: Geo, x: number, z: number, R: number, seed: number, q: number, groundAt: (x: number, z: number) => number, size: number) {
  const n = [1, 2, 4, 5][q] + (k01(hash32(seed, 0x7a1)) * 2 | 0);
  const a0 = k01(hash32(seed, 0x7a2)) * TAU;
  for (let i = 0; i < n; i++) {
    const h = hash32(seed, i, 0x7a3);
    // clustered on one side (where the rock fell), not a ring
    const a = a0 + (k01(h) - 0.5) * 2.4, d = R * (1.0 + 0.45 * k01(h >>> 5));
    const bx = x + Math.cos(a) * d, bz = z + Math.sin(a) * d;
    const gy = groundAt(bx, bz);
    if (!Number.isFinite(gy) || gy < -1e8) continue;
    const r = size * (0.35 + 0.65 * k01(h >>> 9)) * (i ? 0.75 : 1);
    roundBoulder(rock, bx, gy - r * 0.45, bz, r, h, q);
  }
  // chipped debris: small half-buried stones fanned out past the boulders
  const nc = [1, 3, 6, 8][q];
  for (let i = 0; i < nc; i++) {
    const h = hash32(seed, i, 0x7c4);
    const a = a0 + (k01(h) - 0.5) * 3.4, d = R * (1.15 + 0.9 * k01(h >>> 5));
    const bx = x + Math.cos(a) * d, bz = z + Math.sin(a) * d;
    const gy = groundAt(bx, bz);
    if (!Number.isFinite(gy) || gy < -1e8) continue;
    const r = 0.1 + 0.18 * k01(h >>> 9);
    roundBoulder(rock, bx, gy - r * 0.35, bz, r, h, 0);
  }
}
/** A rounded, noise-displaced boulder (icosphere), faceted but never prismatic. */
const _bc = new THREE.Color();
function roundBoulder(rock: Geo, x: number, y: number, z: number, r: number, seed: number, q: number) {
  const g = new THREE.IcosahedronGeometry(1, q >= 2 ? 1 : 0);
  const p = g.attributes.position as THREE.BufferAttribute;
  const sq = 0.62 + 0.25 * k01(seed >>> 3), so = seed & 0xfff;
  const pts: V[] = [];
  for (let i = 0; i < p.count; i++) {
    _bp.fromBufferAttribute(p, i).normalize();
    const k = 1 + (valueNoise(so, _bp.x * 1.3 + _bp.y * 0.7 + 4, _bp.z * 1.3 - _bp.y * 0.9) - 0.5) * 0.5;
    pts.push([x + _bp.x * r * k * 1.15, y + _bp.y * r * k * sq, z + _bp.z * r * k]);
  }
  for (let i = 0; i < pts.length; i += 3) {
    const cy = (pts[i][1] + pts[i + 1][1] + pts[i + 2][1]) / 3;
    const up = smoothstep(y - r * 0.2, y + r * 0.6, cy);
    _bc.copy(cRockEarth).multiplyScalar(0.55 + 0.4 * up);
    const ox = (pts[i][0] + pts[i + 1][0] + pts[i + 2][0]) / 3 - x, oz = (pts[i][2] + pts[i + 1][2] + pts[i + 2][2]) / 3 - z;
    tri(rock, pts[i], pts[i + 1], pts[i + 2], _bc, _bc, _bc, ox, cy - y, oz);
  }
  g.dispose();
}

// ---- arch leg: flares out of the ground and sweeps up (elliptical section, narrowing across,
// swelling toward the span) into the deck. `ex, ez` = unit direction toward the span (caller's hint).
function archLeg(rock: Geo, tops: Geo, c: Cyl, q: number, wseed: number, ground: number, ex: number, ez: number, deckTop: number, cut?: Cut) {
  const seed = hash32(wseed, 0xa7e9, Math.round(c.x * 13.1) * 7919 + Math.round(c.z * 17.3));
  const R = c.r;
  const yTop = Math.max(c.y1, deckTop - 0.3);
  const hSpan = Math.max(1, c.y1 - ground);
  buildColumn(rock, tops, {
    seed, sides: Math.max(9, Math.round(15 * QK[q])),
    ys: colRings(yTop, c.y0, DY[q], 0.2, cut),
    centre: wobble(seed, c.x, c.z, R, 0.04, 0, c.y0),
    radius: (th, y) => {
      const t = Math.min(1.15, Math.max(0, (y - ground) / hSpan));
      const dx = Math.cos(th), dz = Math.sin(th);
      const al = dx * ex + dz * ez, ac = -dx * ez + dz * ex;
      // the mass walks from the outside of the foot to the inside of the shoulder: the leg reads as
      // curving up into the span
      const aIn = 0.7 + 0.4 * smoothstep(0.45, 1.0, t);
      const aOut = 0.86 + 0.24 * smoothstep(0.45, 0, t);
      const b = 0.84 + 0.22 * smoothstep(0.4, 0, t) - 0.2 * smoothstep(0.5, 1.0, t);
      const a = al > 0 ? aIn : aOut;
      let e = 1 / Math.sqrt((al / a) ** 2 + (ac / b) ** 2);
      e *= 1 + (nA(seed + 7, th, y, 1.3, 5) - 0.5) * 0.16 + (nA(seed + 8, th, y, 3.6, 1.6) - 0.5) * 0.08 + (nA(seed + 9, th, y, 6.5, 12) - 0.5) * 0.1;
      return R * e;
    },
    R, maxR: R * 1.1, minR: R * 0.5, bx: c.x, bz: c.z,
    ledges: { seed: seed + 31, G: 2.8, o0: 0.04, o1: 0.1, partial: 0.6, keep: 0.8 },
    paint: { seed, ground, top: yTop + 2, grass: 0, moss: 0.25 },
    crown: 'rock', bottomCap: !!cut,
  });
}

// ---- shelf: a lumpy rounded floating plate, grassy on top (its keel is drawn by keel())
function shelf(rock: Geo, tops: Geo, c: Cyl, q: number, wseed: number) {
  const seed = hash32(wseed, 0x5e1f, Math.round(c.x * 13.1) * 7919 + Math.round(c.z * 17.3));
  const R = c.r, H = c.y1 - c.y0;
  // the last ring sits a little above y0 and pulled in, so the bottom fan (to y0) is a shallow dome
  const ys = ringsY(c.y1, c.y0 + Math.min(0.5, H * 0.15), Math.min(DY[q], H / 4), 0.28);
  buildColumn(rock, tops, {
    seed, sides: Math.max(16, Math.round((40 + 3 * R) * QK[q])),
    ys,
    centre: (_y, out) => { out[0] = c.x; out[1] = c.z; },
    radius: (th, y) => {
      const t = (c.y1 - y) / Math.max(0.5, H); // 0 top -> 1 bottom
      // the outline: broad lobes (consistent top to bottom) + finer chips + a weathered mid scale
      // that grows toward the bowl so the underside silhouette breaks instead of stepping
      let e = 0.94 + (nA(seed + 7, th, 0, 1.4, 99) - 0.5) * 0.2 + (nA(seed + 8, th, y, 4.2, 1.2) - 0.5) * 0.07;
      e *= 1 + (nA(seed + 9, th, y, 2.6, 0.7) - 0.5) * (0.06 + 0.1 * t);
      e *= 1 - 0.1 * t * t - 0.22 * smoothstep(0.75, 1, t); // rounding under into the underside
      if (y > c.y1 - 0.29) e *= 0.95;
      return R * e;
    },
    R, maxR: R * 1.1, minR: R * 0.5, bx: c.x, bz: c.z,
    ledges: { seed: seed + 31, G: 1.2, o0: 0.02, o1: 0.05, partial: 0.7, keep: 0.6 },
    paint: { seed, ground: c.y0 - 4, top: c.y1, grass: 1.0, moss: 0.3, coolFrom: c.y0 + 0.2, coolTo: c.y0 - 5 },
    crown: 'tops', bottomCap: true, bottomDip: Math.min(0.5, H * 0.15),
  });
}

// ---- keel: the shelf's hanging underside -- broken tiers stepping in 8-20% each (the step lines
// wander round the keel, overhangs uneven), then clustered, curved, tapering drips off the tiers
function keel(rock: Geo, tops: Geo, c: Cyl, q: number, wseed: number) {
  const seed = hash32(wseed, 0x6ee1, Math.round(c.x * 13.1) * 7919 + Math.round(c.z * 17.3));
  const R = c.r, L = Math.max(0.5, c.y1 - c.y0);
  const BODY = 1.0;
  // one asymmetric eroded mass: one flank bellies out as it descends while the other is cut back
  const kDir = k01(hash32(seed, 0x6d)) * TAU;
  // the tiers: world-y step lines (a shortened copy keeps them), each stepping the radius in
  const G = 1.15;
  const stepAt = (th: number, y: number) => {
    let f = 1;
    for (let k = Math.floor(c.y0 / G); k <= Math.floor(c.y1 / G); k++) {
      const h = hash32(seed, k, 0x57e9);
      const line = (k + 0.5) * G + (nA(h & 0xfff, th, 0, 2.2, 1) - 0.5) * 0.7;
      if (y < line && line < c.y1 - 0.3) f *= 1 - (0.08 + 0.12 * k01(h >>> 4)) * smoothstep(line, line - 0.22, y);
    }
    return f;
  };
  buildColumn(rock, tops, {
    seed, sides: Math.max(12, Math.round((20 + 2 * R) * QK[q])),
    ys: ringsY(c.y1, c.y1 - L * BODY, DY[q] * 0.6, 0.1),
    centre: wobble(seed, c.x, c.z, R, 0.16, -0.03, c.y1),
    radius: (th, y) => {
      // the tiered body closes off part-way down; a cluster of jagged spikes carries on below
      const u = Math.min(1, (c.y1 - y) / (L * BODY));
      let e = 1.05 * stepAt(th, y) * (1 - 0.3 * u) * Math.pow(Math.max(0, 1 - u), 0.42);
      e *= 1 + 0.32 * Math.cos(th - kDir) * Math.sin(Math.PI * u) - 0.12 * u;
      e *= 1 + (nA(seed + 7, th, y, 1.2, 2.5) - 0.5) * 0.36 + (nA(seed + 8, th, y, 3.8, 1.2) - 0.5) * 0.12;
      return u >= 1 - 1e-3 ? 0 : R * e;
    },
    R, maxR: R * 1.1, minR: 0, bx: c.x, bz: c.z,
    ledges: null,
    paint: { seed, ground: c.y1 - 30, top: c.y1, grass: 0, moss: 0.1, coolFrom: c.y1, coolTo: c.y0 - 1 },
    crown: 'none', bottomCap: false,
  });
  // a few thick, curved drip lobes off the bellied flank (not a field of straight cones)
  const nl = q >= 2 ? 3 : 2;
  for (let i = 0; i < nl; i++) {
    const h = hash32(seed, i, 0x5d);
    const a = kDir + (k01(h) - 0.5) * 1.8;
    const yT = c.y1 - L * (0.3 + 0.3 * k01(h >>> 4));
    const u = (c.y1 - yT) / L;
    const rr = R * 0.6 * (1 - 0.5 * u);
    const len = Math.min(yT - c.y0, L * (0.2 + 0.25 * k01(h >>> 8)));
    if (len < 0.4) continue;
    const r0 = R * (0.22 + 0.12 * k01(h >>> 12));
    drip(rock, c.x + Math.cos(a) * rr, yT + 0.3, c.z + Math.sin(a) * rr, r0, len + 0.3, a, Math.min(R * 1.05 - rr - r0, len * 0.4), seed + 50 + i, q);
  }
  // drips: a cluster of 3-6 curved, tapering stalactites off one side, varied lengths, all inside
  // the keel's footprint and above its y0
  const nd = [2, 3, 5, 6][q];
  const a0 = k01(hash32(seed, 0xd1)) * TAU;
  for (let i = 0; i < nd; i++) {
    const h = hash32(seed, i, 0xd2);
    const a = a0 + (k01(h) - 0.5) * 1.6;
    const yTop = c.y1 - L * (0.12 + 0.3 * k01(h >>> 4));
    const u = (c.y1 - yTop) / L;
    const rr = R * (0.55 + 0.35 * k01(h >>> 8)) * (1 - 0.6 * u);
    const len = Math.min(yTop - c.y0, (0.6 + 2.2 * k01(h >>> 12)) * (R / 2.5));
    if (len < 0.4) continue;
    const r0 = R * (0.1 + 0.1 * k01(h >>> 16));
    drip(rock, c.x + Math.cos(a) * rr, yTop + 0.2, c.z + Math.sin(a) * rr, r0, len, a + (k01(h >>> 20) - 0.5), Math.min(R * 1.05 - rr - r0, len * 0.25), seed + i, q);
  }
}
/** One curved, tapering stalactite: a 5-6 sided tube bending outward as it narrows to a point. */
const _dc = new THREE.Color(), _dc2 = new THREE.Color();
function drip(rock: Geo, x: number, y: number, z: number, r0: number, len: number, bend: number, reach: number, seed: number, q: number) {
  const sides = q >= 2 ? 6 : 5, segs = q >= 2 ? 4 : 3;
  const rings: V[][] = [];
  const ax: V[] = [];
  for (let s = 0; s <= segs; s++) {
    const t = s / segs;
    const off = Math.max(0, reach) * t * t;
    const cx = x + Math.cos(bend) * off, cz = z + Math.sin(bend) * off, cy = y - len * t;
    ax.push([cx, cy, cz]);
    const rr = r0 * Math.pow(1 - t, 0.9);
    const row: V[] = [];
    for (let j = 0; j < sides; j++) {
      const th = (j / sides) * TAU + seed;
      const k = 1 + (k01(hash32(seed, s, j)) - 0.5) * 0.4;
      row.push([cx + Math.cos(th) * rr * k, cy, cz + Math.sin(th) * rr * k]);
    }
    rings.push(row);
  }
  for (let s = 0; s < segs; s++) {
    // warm-dark (r > g) so the strata shader banding + bounce carry onto the drips too
    _dc.copy(cRockEarth).multiplyScalar(0.95 - 0.25 * (s / segs));
    _dc2.copy(cRockEarth).multiplyScalar(0.95 - 0.25 * ((s + 1) / segs));
    for (let j = 0; j < sides; j++) {
      const j1 = (j + 1) % sides;
      const a = rings[s][j], b = rings[s][j1], c2 = rings[s + 1][j1], d = rings[s + 1][j];
      const o = [(a[0] + b[0]) / 2 - ax[s][0], 0, (a[2] + b[2]) / 2 - ax[s][2]];
      tri(rock, a, b, c2, _dc, _dc, _dc2, o[0], o[1], o[2]);
      tri(rock, a, c2, d, _dc, _dc2, _dc2, o[0], o[1], o[2]);
    }
  }
}

// ---- arch deck / natural bridge: a lofted span, rounded ragged edges, a sagging arched
// underside that thins toward mid-span (never below y0), grassy walkable top at exactly y1
function archDeck(rock: Geo, tops: Geo, b: Box, q: number, wseed: number, legs: boolean, piece = false) {
  const seed = hash32(wseed, 0xa4c, Math.round(b.x * 13.1) * 7919 + Math.round(b.z * 17.3));
  const alongX = b.hx > b.hz;
  const L = alongX ? b.hx : b.hz, W = alongX ? b.hz : b.hx;
  const thick = b.y1 - b.y0;
  const toW = (s: number, v: number, y: number): V => (alongX ? [b.x + s, y, b.z + v] : [b.x + v, y, b.z + s]);
  const S = Math.max(8, Math.round(((2 * L) / 1.1) * QK[q])) + 1;
  const Kp = Math.max(8, Math.round(13 * QK[q]));
  // the opening: legged arches spring from the legs (the sim's leg r 2.2 at the span's ends);
  // natural bridges from the canyon walls
  const LEG_R = 2.2;
  const sIn = legs ? Math.max(1, L - LEG_R * 1.85) : Math.max(1, L - 2.5);
  // a natural bridge thins hard toward mid-span (about a third of its abutment thickness)
  const rec = legs ? thick * 0.7 : thick * 0.75;
  const ledges: LedgeSet = { seed: seed + 31, G: legs ? 1.6 : 1.9, o0: 0.04, o1: 0.1, partial: 0.5, keep: 0.75 };
  const pnt: Paint = { seed, ground: b.y0 - 6, top: b.y1, grass: 0.4, moss: 0.3 };
  const lipDrop = (s: number, side: number) => 0.45 * smoothstep(0.35, 0.8, valueNoise(seed + 61 + side * 17, s / 1.6, 0.7)) + 0.12 * valueNoise(seed + 63 + side * 17, s / 0.5, 2.1);
  const P: V[][] = [], G: number[][] = [], ax: V[] = [], vs: number[][] = [];
  for (let i = 0; i < S; i++) {
    const s = -L + (2 * L * i) / (S - 1);
    const u = Math.min(1, Math.abs(s) / sIn);
    // an elliptical opening: springs steeply off the legs, flattens over mid-span
    // legged arches: an ellipse; natural bridges: a continuous hanging curve, thickening all the
    // way out to the walls (no flat constant-thickness middle), its underside lumpy
    // a fallen slab (`piece`) is the deck's full thickness with a lumpy broken underside
    const yU = piece
      ? b.y0 + Math.min(thick * 0.3, 0.6) * valueNoise(seed + 3, s / 1.1, 0.5)
      : legs
      ? b.y0 + rec * Math.pow(Math.max(0, 1 - u * u), 0.6) * (0.85 + 0.15 * valueNoise(seed + 3, s / 1.7, 0.5))
      : Math.max(b.y0, b.y0 + rec * Math.pow(Math.max(0, 1 - u * u), 1.2) * (0.8 + 0.2 * valueNoise(seed + 3, s / 1.3, 0.5)) - 0.5 * (valueNoise(seed + 14, s / 0.9, 1.5) - 0.5));
    // over the legs the span flows down into them (inside the legs' own footprint, so the
    // fly-under clearance between them is untouched): no step where deck meets leg
    const collar = legs && !piece ? 2.4 * smoothstep(L - LEG_R - 1.8, L - LEG_R, Math.abs(s)) : 0;
    // plan: rounded ends, wandering sides
    const endK = Math.sqrt(Math.max(0.0625, 1 - Math.max(0, (Math.abs(s) - (L - 1.4)) / 1.4) ** 2));
    // wandering sides, pinched at mid-span, swelling into abutments where a bridge meets the walls
    const hwBase = W * (0.95 + 0.12 * (valueNoise(seed + 4, s / 4.5, 2.2) - 0.5) * 2 - 0.14 * Math.pow(Math.max(0, 1 - u * u), 2) + (legs ? 0 : 0.1 * smoothstep(0.75, 1, u))) * endK;
    const yc = (b.y1 + yU) / 2;
    const row: V[] = [], gr: number[] = [], vr: number[] = [];
    for (let k = 0; k < Kp; k++) {
      let v: number, y: number;
      if (k === 0 || k === Kp - 1) {
        // the lip: eroded down in lumps along its length (only the outermost edge; the walkable
        // middle of the deck stays at exactly y1)
        v = (k === 0 ? 1 : -1) * hwBase * 0.84;
        y = b.y1 - lipDrop(s, k === 0 ? 0 : 1);
      } else {
        const psi = 0.14 + ((Math.PI - 0.28) * (k - 1)) / (Kp - 3);
        const cs = Math.cos(psi), sn = Math.sin(psi);
        v = hwBase * Math.sign(cs) * Math.pow(Math.abs(cs), 0.55);
        y = b.y1 - (b.y1 - (yU - collar)) * Math.pow(sn, 0.6);
        // eroded underside: scooped pockets (upward only, so never below y0)
        y += (b.y1 - yU) * (legs ? 0.35 : 0.45) * Math.pow(sn, 6) * valueNoise(seed + 13, s / 1.4, cs * 2.5) ** 2;
        const side = Math.sign(v) || 1;
        const n2 = (valueNoise(seed + 9 + (side > 0 ? 0 : 50), s / 2.3 + y * 0.4, y / 1.6) - 0.5) * 0.16;
        const lg = ledgeAt(ledges, side > 0 ? 0.3 + s * 0.08 : 3.4 + s * 0.08, y);
        v *= 1 + n2 + lg.g;
        gr.push(lg.groove);
        v = Math.max(-W * 1.1, Math.min(W * 1.1, v));
        y = Math.max(b.y0 - collar, Math.min(b.y1, y));
        row.push(toW(s, v, y)); vr.push(v);
        continue;
      }
      gr.push(0);
      row.push(toW(s, v, y)); vr.push(v);
    }
    P.push(row); G.push(gr); vs.push(vr);
    ax.push(toW(s, 0, yc));
  }
  const cols: THREE.Color[][] = [];
  for (let i = 0; i < S; i++) {
    const row: THREE.Color[] = [];
    for (let k = 0; k < Kp; k++) {
      const p = P[i][k];
      const ny = gridNy(P, i, k, false, ax[i]);
      row.push(paint(pnt, p[0], p[1], p[2], 0, ny, G[i][k], new THREE.Color()));
    }
    cols.push(row);
  }
  // the loft runs across stations (i) and round the section (k); emitGrid's "rings" are stations
  emitGrid(rock, P, cols, false, (i) => ax[Math.min(i, S - 1)]);
  // end caps (hidden in the legs / the canyon walls, but closed)
  for (const i of [0, S - 1]) {
    const row = P[i], cA = ax[i];
    const o = alongX ? [i === 0 ? -1 : 1, 0, 0] : [0, 0, i === 0 ? -1 : 1];
    for (let k = 0; k < Kp - 1; k++) tri(rock, cA, row[k], row[k + 1], cols[i][k], cols[i][k], cols[i][k + 1], o[0], o[1], o[2]);
    tri(rock, cA, row[Kp - 1], row[0], cols[i][0], cols[i][0], cols[i][0], o[0], o[1], o[2]);
  }
  // a natural bridge drips: clusters of short stalactites off the thin middle of its underside,
  // never reaching below the box's y0
  if (!legs && !piece) {
    const nd = [2, 3, 5, 7][q];
    for (let i = 0; i < nd; i++) {
      const h = hash32(seed, i, 0xd7);
      const s = (k01(h) - 0.5) * 2 * L * 0.6;
      const u = Math.min(1, Math.abs(s) / sIn);
      const yU = b.y0 + rec * Math.pow(Math.max(0, 1 - u * u), 1.2) * 0.8;
      const room = yU - b.y0 - 0.15;
      if (room < 0.5) continue;
      const v = (k01(h >>> 6) - 0.5) * W * 0.7;
      const p = toW(s, v, yU + 0.35);
      drip(rock, p[0], p[1], p[2], 0.16 + 0.18 * k01(h >>> 10), Math.min(room, 0.6 + 1.2 * k01(h >>> 14)), k01(h >>> 18) * TAU, 0.25, seed + i, q);
    }
  }
  // the grassy deck (tops): a worn trail down the middle, deep green at the edges
  const fr = [-1, -0.55, -0.12, 0.12, 0.55, 1];
  const at = (i: number, f: number): V => {
    const s = -L + (2 * L * i) / (S - 1);
    const w = f > 0 ? vs[i][0] : -vs[i][Kp - 1];
    // the outermost strip follows the eroded lip down; everything inside it is at y1
    return toW(s, f * w * 1.01, b.y1 + 0.02 - (Math.abs(f) > 0.99 ? lipDrop(s, f > 0 ? 0 : 1) : 0));
  };
  const col = (p: V, f: number, out: THREE.Color) => {
    crownCol(p[0], p[2], Math.abs(f), seed, out);
    if (Math.abs(f) < 0.2) out.lerp(cDirt, 0.5);
    return out;
  };
  for (let i = 0; i < S - 1; i++) {
    for (let j = 0; j < fr.length - 1; j++) {
      const a = at(i, fr[j]), bb = at(i + 1, fr[j]), c = at(i + 1, fr[j + 1]), d = at(i, fr[j + 1]);
      col(a, fr[j], cc0); col(bb, fr[j], cc1); col(c, fr[j + 1], cc2); col(d, fr[j + 1], cc3);
      tri(tops, a, bb, c, cc0, cc1, cc2, 0, 1, 0);
      tri(tops, a, c, d, cc0, cc2, cc3, 0, 1, 0);
    }
  }
}

// ------------------------------------------------------------------ entry
/** Where each spire / arch (by variant) sits in the built geometry: vertex [start, count] runs, so a toppled one can be hidden (world/stones.ts). */
export type LandformRuns = Map<number, { rock: number[][]; tops: number[][] }>;

export function buildLandforms(world: World, quality: number, runs?: LandformRuns): LandformGeo {
  const q = Math.max(0, Math.min(3, quality | 0));
  const rock = new Geo(), tops = new Geo();
  const ws = world.seed;
  // caller-side hints (each shape function only sees its own Cyl/Box)
  const deckOf = new Map<number, Box>();
  for (const b of world.boxes) if (b.kind === 'arch') deckOf.set(b.variant, b);
  const legVariants = new Set<number>();
  const groundAt = (x: number, z: number) => world.terrain(x, z);
  // records each spire / arch's vertex runs (by variant) so a toppled one can be hidden
  // (world/stones.ts); weldNormals below only rewrites normals, so the runs stay valid
  const track = (variant: number, draw: () => void) => {
    const r0 = rock.pos.length / 3, t0 = tops.pos.length / 3;
    draw();
    if (!runs) return;
    let e = runs.get(variant);
    if (!e) runs.set(variant, (e = { rock: [], tops: [] }));
    e.rock.push([r0, rock.pos.length / 3 - r0]);
    e.tops.push([t0, tops.pos.length / 3 - t0]);
  };
  for (const c of world.cyls) {
    // the ground the sim buried the base in: spires sit 1.5 m deep, legs 1.2 m
    if (c.kind === 'spire') {
      const ground = c.y0 + 1.5;
      if (isHoodoo(c, ground)) track(c.variant, () => hoodoo(rock, tops, c, q, ws, ground, groundAt));
      else track(c.variant, () => spire(rock, tops, c, q, ws, ground, groundAt));
    } else if (c.kind === 'archleg') {
      legVariants.add(c.variant);
      const [ex, ez, deckTop] = legHint(c, deckOf.get(c.variant));
      track(c.variant, () => archLeg(rock, tops, c, q, ws, c.y0 + 1.2, ex, ez, deckTop));
    } else if (c.kind === 'shelf') shelf(rock, tops, c, q, ws);
    else if (c.kind === 'keel') keel(rock, tops, c, q, ws);
  }
  for (const b of world.boxes) if (b.kind === 'arch') track(b.variant, () => archDeck(rock, tops, b, q, ws, legVariants.has(b.variant)));
  weldNormals(rock, 0.25);
  return { rock: rock.build(), tops: tops.build() };
}

const isHoodoo = (c: Cyl, ground: number) => c.r < 3 && c.y1 - ground < 13;
/** An arch leg's caller-side hint: the unit direction toward its span and the deck's top. */
function legHint(c: Cyl, b: Box | undefined): [number, number, number] {
  if (!b) return [1, 0, c.y1];
  const dx = b.x - c.x, dz = b.z - c.z, d = Math.hypot(dx, dz) || 1;
  return [dx / d, dz / d, b.y1];
}

/**
 * One piece of a toppled spire or arch (world/stones.ts), built by the same shape functions as
 * the standing landform, so it is the same rock: a drum of the column is the spire / hoodoo / leg
 * profile of the WHOLE column (`whole`), cut to the drum's height and closed with broken faces
 * (`crown` keeps the grassy top); a deck slab is archDeck on the cut Box. World coordinates.
 */
export function landformPiece(world: World, part: { cyl?: Cyl; box?: Box; whole?: { y0: number; y1: number } }, quality: number, crown: boolean): LandformGeo {
  const q = Math.max(0, Math.min(3, quality | 0));
  const rock = new Geo(), tops = new Geo();
  const ws = world.seed;
  if (part.cyl) {
    const p = part.cyl;
    // the standing column this drum came from (same footprint centre)
    const src = world.cyls.find((c) => c.kind === p.kind && Math.abs(c.x - p.x) < 1e-6 && Math.abs(c.z - p.z) < 1e-6);
    const whole = part.whole ?? (src ? { y0: src.y0, y1: src.y1 } : { y0: p.y0, y1: p.y1 });
    const full: Cyl = { ...p, y0: whole.y0, y1: whole.y1 };
    const cut: Cut = { y0: p.y0, y1: p.y1, crown };
    if (p.kind === 'archleg') {
      let deck: Box | undefined;
      if (src) for (const b of world.boxes) if (b.kind === 'arch' && b.variant === src.variant) { deck = b; break; }
      const [ex, ez, deckTop] = legHint(full, deck);
      archLeg(rock, tops, full, q, ws, full.y0 + 1.2, ex, ez, deckTop, cut);
    } else {
      const ground = full.y0 + 1.5;
      if (isHoodoo(src ?? full, ground)) hoodoo(rock, tops, full, q, ws, ground, undefined, cut);
      else spire(rock, tops, full, q, ws, ground, undefined, cut);
    }
  } else if (part.box) archDeck(rock, tops, part.box, q, ws, true, true);
  weldNormals(rock, 0.25);
  return { rock: rock.build(), tops: tops.build() };
}

export { spire as buildSpire, hoodoo as buildHoodoo, archLeg as buildArchLeg, archDeck as buildArchDeck, shelf as buildShelf, keel as buildKeel };
